/**
 * Override form validation to only check visible fields
 *
 * NOTE: This complex validation approach was implemented because:
 * 1. The form has dynamic sections that show/hide based on user selection
 * 2. Hidden sections contain required fields that would normally block form submission
 * 3. We need to validate only visible required fields to provide a better UX
 * 4. Web components (uo-text-field, etc.) have their own validation methods
 *
 * TODO: This should be simplified in a future refactor. Consider:
 * - Moving to a config-driven validation system
 * - Using a form library that handles dynamic validation
 * - Implementing proper form state management
 * @param form
 */
export const overrideFormValidation = (form) => {
    form.checkValidity = () => {
        // Only check visible required fields
        const visibleRequiredFields = getVisibleRequiredFields(form);
        return visibleRequiredFields.every(field => validateField(field));
    };
    form.reportValidity = () => {
        // Only report validity for visible required fields
        const visibleRequiredFields = getVisibleRequiredFields(form);
        let isValid = true;
        visibleRequiredFields.forEach(field => {
            if (!validateField(field)) {
                isValid = false;
                reportFieldValidity(field);
            }
        });
        return isValid;
    };
};
/**
 * Validate a single field (handles both native and web components)
 *
 * NOTE: This method handles two types of form elements:
 * 1. Native HTML form elements (input, select, textarea) - use built-in validation
 * 2. Web components (uo-text-field, etc.) - check value manually since they don't have native validation
 * @param field
 */
export const validateField = (field) => {
    // For native form elements
    if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
        if (field.checkValidity && typeof field.checkValidity === 'function') {
            return field.checkValidity();
        }
    }
    // For web components
    if (field.tagName.includes('-')) {
        const webComponent = field;
        if (webComponent.required) {
            // Check if web component has a value
            return !!(webComponent.value && webComponent.value.trim() !== '');
        }
    }
    return true;
};
/**
 * Report validity for a single field (handles both native and web components)
 *
 * NOTE: This method triggers error display for invalid fields:
 * 1. Native HTML form elements - use built-in reportValidity() which shows browser validation messages
 * 2. Web components - use their custom reportValidity() method which adds error styling
 * @param field
 */
export const reportFieldValidity = (field) => {
    // For native form elements
    if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
        if (field.reportValidity && typeof field.reportValidity === 'function') {
            field.reportValidity();
            return;
        }
    }
    // For web components, use their native reportValidity method
    if (field.tagName.includes('-')) {
        const webComponent = field;
        if (webComponent.reportValidity && typeof webComponent.reportValidity === 'function') {
            webComponent.reportValidity();
        }
    }
};
/**
 * Get only visible required fields for validation
 *
 * NOTE: This function is complex because we need to:
 * 1. Find all required fields in the form
 * 2. Filter out fields that are hidden by dynamic sections
 * 3. Handle both native HTML elements and web components
 * 4. Ensure web components are included for validation even if they don't have native validation methods
 *
 * TODO: This should be simplified when moving to a proper form state management system
 * @param form
 */
export const getVisibleRequiredFields = (form) => {
    const requiredFields = form.querySelectorAll('[required]');
    const visibleFields = Array.from(requiredFields).filter((field) => {
        // Use the same visibility logic as isFieldVisible
        if (!isFieldVisible(field, form)) {
            return false;
        }
        // For web components, check if they have validation methods or are valid
        if (field.tagName.includes('-')) {
            // Web components - check if they have validation or are valid
            const webComponent = field;
            if (webComponent.checkValidity && typeof webComponent.checkValidity === 'function') {
                return true; // Has native validation
            }
            // For web components without native validation, check if they have value
            if (webComponent.required && (!webComponent.value || webComponent.value.trim() === '')) {
                return true; // Include for validation (will be handled by custom logic)
            }
            return false;
        }
        // For native form elements, ensure they have checkValidity method
        return !!field.checkValidity && typeof field.checkValidity === 'function';
    });
    return visibleFields;
};
/**
 * Check if a field is visible (not hidden by section toggle)
 * @param field
 * @param form
 */
const isFieldVisible = (field, form) => {
    // Check if the field itself is hidden
    if (field instanceof HTMLElement && (field.style.display === 'none' || field.hidden)) {
        return false;
    }
    // Check if parent section is hidden
    let parent = field.parentElement;
    while (parent && parent !== form) {
        // Check for hidden section components
        if (parent.tagName === 'UAP-APP-INTEGRATION-SETTINGS-SECTION') {
            const section = parent;
            if (section.visible === false) {
                return false;
            }
        }
        // Also check for CSS hidden sections
        if (parent.classList.contains('section') && parent instanceof HTMLElement && parent.style.display === 'none') {
            return false;
        }
        parent = parent.parentElement;
    }
    return true;
};
